// src/pages/admin/CreditManagerPage.js — grant account credit (Stripe
// customer balance) and audit the ledger. Negative amounts are corrections.
import React, { useState, useEffect, useCallback } from 'react';
import { formatMoney } from '../../lib/formatMoney';

const API = process.env.REACT_APP_API_URL;

const inputClass = 'bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm placeholder-neutral-600 focus:ring-2 focus:ring-cyan-500/60 focus:outline-none';

const CreditManagerPage = () => {
    const [rows, setRows] = useState([]);
    const [liveBalance, setLiveBalance] = useState(null);
    const [filterEmail, setFilterEmail] = useState('');
    const [activeFilter, setActiveFilter] = useState('');
    const [email, setEmail] = useState('');
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const fetchLedger = useCallback(async (emailFilter) => {
        const qs = emailFilter ? `?email=${encodeURIComponent(emailFilter)}` : '';
        try {
            const res = await fetch(`${API}/api/admin_list_credit_ledger.php${qs}`, { credentials: 'include' });
            const data = await res.json();
            if (res.ok && data.status === 'success') {
                setRows(data.rows);
                setLiveBalance(data.live_balance_cents);
            } else {
                setMessage({ type: 'error', text: data.message || 'Could not load the ledger.' });
            }
        } catch (e) {
            setMessage({ type: 'error', text: 'Could not load the ledger.' });
        }
    }, []);

    useEffect(() => { fetchLedger(''); }, [fetchLedger]);

    const submitGrant = async (e) => {
        e.preventDefault();
        const cents = Math.round(parseFloat(amount) * 100);
        if (!email.trim() || !Number.isFinite(cents) || cents === 0) {
            setMessage({ type: 'error', text: 'Enter an email and a non-zero amount.' });
            return;
        }
        setBusy(true);
        setMessage({ type: '', text: '' });
        try {
            const res = await fetch(`${API}/api/admin_grant_credit.php`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ email: email.trim(), amount_cents: cents, ...(note.trim() ? { note: note.trim() } : {}) }),
            });
            const data = await res.json();
            if (res.ok && data.status === 'success') {
                setMessage({ type: 'success', text: `Credit updated — new balance ${formatMoney(data.new_balance_cents, 'usd')}.` });
                setAmount('');
                setNote('');
                fetchLedger(activeFilter);
            } else {
                setMessage({ type: 'error', text: data.message || 'Could not grant credit.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Network error. Please try again.' });
        } finally {
            setBusy(false);
        }
    };

    const applyFilter = (e) => {
        e.preventDefault();
        const f = filterEmail.trim();
        setActiveFilter(f);
        setLiveBalance(null);
        fetchLedger(f);
    };

    return (
        <div className="text-white max-w-6xl">
            <header className="mb-8">
                <h1 className="text-3xl font-bold tracking-tight">Credits</h1>
                <p className="text-sm text-neutral-400 mt-1">
                    Grant account credit — it pays down future invoices automatically. Negative amounts correct mistakes; don't drive a balance below zero.
                </p>
            </header>

            {message.text && (
                <p className={`mb-4 text-sm ${message.type === 'error' ? 'text-red-400' : 'text-emerald-400'}`}>{message.text}</p>
            )}

            <section className="rounded-2xl border border-white/10 bg-[#0b0e14]/60 backdrop-blur-sm p-6 mb-6">
                <form onSubmit={submitGrant} className="flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[200px]">
                        <label className="block text-xs text-neutral-400 mb-1">User email</label>
                        <input className={`${inputClass} w-full`} placeholder="user email" value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>
                    <div className="w-32">
                        <label className="block text-xs text-neutral-400 mb-1">Amount ($)</label>
                        <input className={`${inputClass} w-full`} placeholder="amount" value={amount} onChange={(e) => setAmount(e.target.value)} />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <label className="block text-xs text-neutral-400 mb-1">Note (optional)</label>
                        <input className={`${inputClass} w-full`} placeholder="note" value={note} onChange={(e) => setNote(e.target.value)} />
                    </div>
                    <button type="submit" disabled={busy}
                        className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-2 px-5 rounded-lg disabled:opacity-50 transition-colors">
                        {busy ? 'Granting…' : 'Grant credit'}
                    </button>
                </form>
            </section>

            <section className="rounded-2xl border border-white/10 bg-[#0b0e14]/60 backdrop-blur-sm p-6">
                <form onSubmit={applyFilter} className="mb-4 flex items-center gap-3 flex-wrap">
                    <input className={`${inputClass} flex-1 min-w-[200px]`} placeholder="filter by email" value={filterEmail} onChange={(e) => setFilterEmail(e.target.value)} />
                    <button type="submit" className="bg-white/5 hover:bg-white/10 border border-white/10 text-sm py-2 px-4 rounded-lg transition-colors">Filter</button>
                    {activeFilter && liveBalance !== null && (
                        <span className="text-sm text-emerald-300">Current balance: {formatMoney(liveBalance, 'usd')}</span>
                    )}
                </form>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-neutral-500 border-b border-white/10">
                            <th className="py-2 font-medium">Email</th>
                            <th className="py-2 font-medium">Amount</th>
                            <th className="py-2 font-medium">Source</th>
                            <th className="py-2 font-medium">Note</th>
                            <th className="py-2 font-medium">Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-b border-white/5 last:border-0">
                                <td className="py-2.5 text-neutral-300">{r.email}</td>
                                <td className={`py-2.5 ${r.amount_cents < 0 ? 'text-amber-300' : 'text-emerald-300'}`}>
                                    {r.amount_cents < 0 ? `-${formatMoney(Math.abs(r.amount_cents), 'usd')}` : formatMoney(r.amount_cents, 'usd')}
                                </td>
                                <td className="py-2.5 text-neutral-400">{r.source === 'gift_card' ? 'Gift card' : 'Admin grant'}</td>
                                <td className="py-2.5 text-neutral-400">{r.note || '—'}</td>
                                <td className="py-2.5 text-neutral-400">{String(r.created_at).split(' ')[0]}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan="5" className="py-6 text-center text-neutral-500">No credit activity yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </section>
        </div>
    );
};

export default CreditManagerPage;
